import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { WebSettingsService } from '../services/WebSettingsService';
import { WebSettings } from '../types/WebSettings';

interface WebSettingsContextType {
  webSettings: WebSettings;
  updateWebSettings: (newSettings: Partial<WebSettings>) => Promise<void>;
  loading: boolean;
}

const WebSettingsContext = createContext<WebSettingsContextType | undefined>(undefined);

export const WebSettingsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [webSettings, setWebSettings] = useState<WebSettings>({
    description: {
      title: { eng: '', fr: '', ar: '' },
      body: { eng: '', fr: '', ar: '' }
    },
    helpAndSupport: { eng: '', fr: '', ar: '' },
    privacyPolicy: { eng: '', fr: '', ar: '' },
    termsAndConditions: { eng: '', fr: '', ar: '' },
    contactDetails: {
      email: '',
      phone: '',
      location: ''
    },
    socials: {
      instagramUrl: '',
      facebookUrl: '',
      youtubeUrl: '',
      whatsappUrl: '',
      XUrl: ''
    },
    topPurchasedBookSection: false,
    _id: '' 
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchWebSettings = async () => {
      try {
        const response = await WebSettingsService.getWebSettings();
        setWebSettings(response.webSettings);
      } catch (error) {
        console.error('Error fetching web settings:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchWebSettings();
  }, []);

  const updateWebSettings = useCallback(async (newSettings: Partial<WebSettings>) => {
    try {
      // Create a deep merge of the settings
      const updatedSettings = {
        ...webSettings,
        ...newSettings,
        description: {
          ...webSettings.description,
          ...(newSettings.description || {})
        },
        helpAndSupport: {
          ...webSettings.helpAndSupport,
          ...(newSettings.helpAndSupport || {})
        },
        privacyPolicy: {
          ...webSettings.privacyPolicy,
          ...(newSettings.privacyPolicy || {})
        },
        termsAndConditions: {
          ...webSettings.termsAndConditions,
          ...(newSettings.termsAndConditions || {})
        },
        contactDetails: {
          ...webSettings.contactDetails,
          ...(newSettings.contactDetails || {})
        },
        socials: {
          ...webSettings.socials,
          ...(newSettings.socials || {})
        }
      };

      // Update the settings in the backend
      await WebSettingsService.updateWebSettings(updatedSettings, webSettings._id);
      
      // Update local state
      setWebSettings(updatedSettings);
    } catch (error) {
      console.error('Error updating web settings:', error);
      throw new Error('Failed to update web settings. Please try again.');
    }
  }, [webSettings]);

  return (
    <WebSettingsContext.Provider value={{ webSettings, updateWebSettings, loading }}>
      {children}
    </WebSettingsContext.Provider>
  );
};

export const useWebSettings = () => {
  const context = useContext(WebSettingsContext);
  if (context === undefined) {
    throw new Error('useWebSettings must be used within a WebSettingsProvider');
  }
  return context;
}; 